import { h } from 'preact';
import { useState, useEffect, useMemo, useRef, useCallback } from 'preact/hooks';
import type { VertexWithMetadata, VertexSetWithVertices } from '../types';
import * as api from '../services/api';
import { EditableNote } from './EditableNote';
import { VertexPreviewCard } from './VertexPreviewCard';
import { StatsBar } from './shared/StatsBar';
import { GlobeIcon, ArchiveIcon, CopyIcon, DeleteIcon, CheckIcon, GridIcon, GlobeOutlineIcon, EdgeChainIcon, SetIcon } from './icons';
// @ts-ignore
import { CHROME_EXTENSION_URL } from '../../constants.js';
import { OutlinedButton } from './shared/OutlinedButton';
// @ts-ignore
import { copyWithFeedback } from '../../shared.js';

type ViewMode = 'flat' | 'authority' | 'auto';

const AUTO_AUTHORITY_THRESHOLD = 15;

// Chip-fetch tuning: cards enter viewport → URL added to pending → flushed in batches.
const CHIP_FETCH_ROOT_MARGIN = '200px 0px';
const CHIP_FETCH_DEBOUNCE_MS = 150;
const CHIP_FETCH_MAX_BATCH = 50;

function mergeSetsById(
  prev: VertexSetWithVertices[],
  next: VertexSetWithVertices[],
): VertexSetWithVertices[] {
  const byId = new Map(prev.map((s) => [s.vertexSet.id, s]));
  for (const s of next) byId.set(s.vertexSet.id, s);
  return Array.from(byId.values()).sort(
    (a, b) =>
      new Date(b.vertexSet.updatedAt).getTime() - new Date(a.vertexSet.updatedAt).getTime(),
  );
}

interface AuthorityGroup {
  authority: string;
  vertices: VertexWithMetadata[];
  previewImage: string | null;
  latestUpdate: string;
}

interface VertexListProps {
  searchQuery: string;
  viewMode: ViewMode;
  selectedAuthority: string | null;
  onViewModeChange: (mode: ViewMode, authority: string | null) => void;
  onViewArchive: (url: string, timestamp: string, availableTimes: string[]) => void;
  onOpenGraph: (url: string) => void;
  onOpenSet: (setId: string) => void;
}


export function VertexList({ searchQuery, viewMode, selectedAuthority, onViewModeChange, onViewArchive, onOpenGraph, onOpenSet }: VertexListProps) {
  const [vertices, setVertices] = useState<VertexWithMetadata[]>([]);
  const [filteredVertices, setFilteredVertices] = useState<VertexWithMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [pendingBackendCapture, setPendingBackendCapture] = useState<Set<string>>(new Set());
  const [captureQuota, setCaptureQuota] = useState<api.CaptureQuotaReport | null>(null);

  // Vertex sets state
  const [setsDropdownOpen, setSetsDropdownOpen] = useState<string | null>(null);
  const [vertexSets, setVertexSets] = useState<VertexSetWithVertices[]>([]);
  const [queriedUrls, setQueriedUrls] = useState<Set<string>>(new Set());
  const [allSetsLoaded, setAllSetsLoaded] = useState(false);
  const [setsSearchQuery, setSetsSearchQuery] = useState('');
  const [creatingNewSet, setCreatingNewSet] = useState(false);
  const [newSetName, setNewSetName] = useState('');
  const [highlightedSetIndex, setHighlightedSetIndex] = useState(-1);
  const [setsLoading, setSetsLoading] = useState(false);
  const setsSearchRef = useRef<HTMLInputElement>(null);

  // Load all data
  useEffect(() => {
    loadVertices();
  }, []);

  // Fetch today's server-capture quota so the button can reflect it.
  useEffect(() => {
    api
      .fetchArchiveSourcesToday()
      .then((r) => setCaptureQuota(r.captureQuota))
      .catch(() => {});
  }, []);

  // Auto-select view mode on first load (when no explicit view param)
  useEffect(() => {
    if (viewMode === 'auto' && !loading) {
      if (vertices.length > AUTO_AUTHORITY_THRESHOLD) {
        onViewModeChange('authority', null);
      } else {
        onViewModeChange('flat', null);
      }
    }
  }, [viewMode, loading, vertices.length]);

  // Effective view mode for rendering ('auto' behaves as 'flat' when below threshold)
  const effectiveViewMode: 'flat' | 'authority' = viewMode === 'authority' ? 'authority' : 'flat';

  // Filter vertices when search changes
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredVertices(vertices);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = vertices.filter((v) => {
      const urlMatch = v.vertex.rendered.toLowerCase().includes(query);
      const noteMatch = v.note?.toLowerCase().includes(query);
      const titleMatch = v.preview?.title?.toLowerCase().includes(query);
      const descMatch = v.preview?.description?.toLowerCase().includes(query);
      return urlMatch || noteMatch || titleMatch || descMatch;
    });

    setFilteredVertices(filtered);
  }, [searchQuery, vertices]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClick = () => {
      if (openDropdown) setOpenDropdown(null);
      if (setsDropdownOpen) {
        setSetsDropdownOpen(null);
        setSetsSearchQuery('');
        setCreatingNewSet(false);
        setNewSetName('');
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [openDropdown, setsDropdownOpen]);

  // Focus search input when sets dropdown opens
  useEffect(() => {
    if (setsDropdownOpen && setsSearchRef.current) {
      setsSearchRef.current.focus();
    }
  }, [setsDropdownOpen]);

  // Reset keyboard highlight whenever the dropdown opens/closes or the search filter changes.
  useEffect(() => {
    setHighlightedSetIndex(-1);
  }, [setsDropdownOpen, setsSearchQuery]);

  // Scroll the highlighted dropdown item into view.
  useEffect(() => {
    if (highlightedSetIndex < 0) return;
    const el = document.querySelector(
      `.vertex-sets-dropdown [data-set-idx="${highlightedSetIndex}"]`,
    );
    (el as HTMLElement | null)?.scrollIntoView({ block: 'nearest' });
  }, [highlightedSetIndex]);

  // Group vertices by authority
  const authorityGroups = useMemo((): AuthorityGroup[] => {
    const groups = new Map<string, VertexWithMetadata[]>();

    for (const v of filteredVertices) {
      const authority = v.vertex.components.authority;
      if (!groups.has(authority)) {
        groups.set(authority, []);
      }
      groups.get(authority)!.push(v);
    }

    // Convert to array and compute preview image for each group
    const result: AuthorityGroup[] = [];
    for (const [authority, verts] of groups) {
      // Sort by updatedAt descending to get latest first
      const sorted = [...verts].sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );

      // Find first vertex with a preview image
      const withImage = sorted.find((v) => v.preview?.image);
      const previewImage = withImage?.preview?.image || null;

      result.push({
        authority,
        vertices: sorted,
        previewImage,
        latestUpdate: sorted[0].updatedAt,
      });
    }

    // Sort groups by latest update
    result.sort((a, b) => new Date(b.latestUpdate).getTime() - new Date(a.latestUpdate).getTime());

    return result;
  }, [filteredVertices]);

  // Get vertices to display (filtered by selected authority if in authority mode)
  const displayVertices = useMemo(() => {
    if (effectiveViewMode === 'authority' && selectedAuthority) {
      return filteredVertices.filter((v) => v.vertex.components.authority === selectedAuthority);
    }
    return filteredVertices;
  }, [effectiveViewMode, selectedAuthority, filteredVertices]);

  // Lazily fetch set memberships for cards that actually enter the viewport.
  // Cards register via cardRefCallback; an IntersectionObserver watches them and batches
  // URL lookups. Skipped on the authority grid (no cards) and once allSetsLoaded is true.
  const observerRef = useRef<IntersectionObserver | null>(null);
  const cardElementsRef = useRef<Map<string, Element>>(new Map());
  const cardRefCallbacksRef = useRef<Map<string, (el: Element | null) => void>>(new Map());
  const pendingUrlsRef = useRef<Set<string>>(new Set());
  const flushTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const queriedUrlsRef = useRef<Set<string>>(queriedUrls);
  const allSetsLoadedRef = useRef<boolean>(allSetsLoaded);

  useEffect(() => {
    queriedUrlsRef.current = queriedUrls;
  }, [queriedUrls]);
  useEffect(() => {
    allSetsLoadedRef.current = allSetsLoaded;
  }, [allSetsLoaded]);

  const flushPending = useCallback(() => {
    flushTimeoutRef.current = null;
    if (allSetsLoadedRef.current) {
      pendingUrlsRef.current.clear();
      return;
    }
    const batch = Array.from(pendingUrlsRef.current)
      .filter((u) => !queriedUrlsRef.current.has(u))
      .slice(0, CHIP_FETCH_MAX_BATCH);
    for (const u of batch) pendingUrlsRef.current.delete(u);
    if (batch.length === 0) return;

    api
      .fetchAllVertexSets(batch)
      .then((sets) => {
        setVertexSets((prev) => mergeSetsById(prev, sets));
        setQueriedUrls((prev) => {
          const next = new Set(prev);
          for (const u of batch) next.add(u);
          return next;
        });
        if (pendingUrlsRef.current.size > 0 && !flushTimeoutRef.current) {
          flushTimeoutRef.current = setTimeout(flushPending, CHIP_FETCH_DEBOUNCE_MS);
        }
      })
      .catch((err) => {
        console.error('Failed to load set memberships:', err);
      });
  }, []);

  const scheduleFlush = useCallback(() => {
    if (flushTimeoutRef.current) return;
    flushTimeoutRef.current = setTimeout(flushPending, CHIP_FETCH_DEBOUNCE_MS);
  }, [flushPending]);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (allSetsLoadedRef.current) return;
        let added = false;
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const url = (entry.target as HTMLElement).dataset.vertexUrl;
          if (!url) continue;
          if (queriedUrlsRef.current.has(url)) continue;
          if (pendingUrlsRef.current.has(url)) continue;
          pendingUrlsRef.current.add(url);
          added = true;
        }
        if (added) scheduleFlush();
      },
      { rootMargin: CHIP_FETCH_ROOT_MARGIN },
    );
    observerRef.current = observer;
    for (const el of cardElementsRef.current.values()) observer.observe(el);
    return () => {
      observer.disconnect();
      observerRef.current = null;
      if (flushTimeoutRef.current) {
        clearTimeout(flushTimeoutRef.current);
        flushTimeoutRef.current = null;
      }
      pendingUrlsRef.current.clear();
    };
  }, [scheduleFlush]);

  // Stable per-URL ref callback so re-renders don't churn observe/unobserve.
  function getCardRef(url: string) {
    let cb = cardRefCallbacksRef.current.get(url);
    if (cb) return cb;
    cb = (el: Element | null) => {
      if (el) {
        (el as HTMLElement).dataset.vertexUrl = url;
        cardElementsRef.current.set(url, el);
        observerRef.current?.observe(el);
      } else {
        const existing = cardElementsRef.current.get(url);
        if (existing) observerRef.current?.unobserve(existing);
        cardElementsRef.current.delete(url);
      }
    };
    cardRefCallbacksRef.current.set(url, cb);
    return cb;
  }

  async function loadVertices() {
    try {
      setLoading(true);
      setError(null);

      const allVertices = await api.fetchAllVertices();

      // Sort by createdAt descending (latest first)
      allVertices.sort((a, b) => {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return dateB - dateA;
      });

      if (allVertices.length === 0) {
        setVertices([]);
        setFilteredVertices([]);
        setLoading(false);
        return;
      }

      // Fetch metadata in parallel - use rendered URL strings
      const urls = allVertices.map((v) => v.vertex.rendered);
      const [archiveTimes, previews] = await Promise.all([
        api.fetchArchiveTimesBatch(urls),
        api.fetchPreviewsBatch(urls),
      ]);

      // Merge metadata
      const withMetadata: VertexWithMetadata[] = allVertices.map((v) => ({
        ...v,
        archiveTimes: archiveTimes[v.vertex.rendered] || [],
        preview: previews[v.vertex.rendered],
      }));

      setVertices(withMetadata);
      setFilteredVertices(withMetadata);
    } catch (err) {
      console.error('Failed to load vertices:', err);
      setError(err instanceof Error ? err.message : 'Failed to load vertices');
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteVertex(url: string) {
    if (!confirm(`Delete vertex: ${url}?`)) return;

    try {
      await api.deleteVertices([url]);
      setVertices((prev) => prev.filter((v) => v.vertex.rendered !== url));
    } catch (err) {
      alert(`Failed to delete: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  async function handleSaveNote(url: string, newNote: string | null) {
    const response = await api.updateVertex(url, newNote);

    // Update local state with the response from backend (includes updated timestamp)
    setVertices((prev) =>
      prev.map((v) =>
        v.vertex.rendered === url
          ? { ...v, note: newNote, updatedAt: response.updatedAt }
          : v
      )
    );
  }

  function toggleArchiveDropdown(url: string, e: Event) {
    e.stopPropagation();
    e.preventDefault();
    setOpenDropdown(openDropdown === url ? null : url);
  }

  function handleBackendCapture(url: string, e: Event) {
    e.stopPropagation();
    if (pendingBackendCapture.has(url)) return;
    setOpenDropdown(null);
    setPendingBackendCapture((prev) => new Set(prev).add(url));
    api
      .backendArchiveVertex(url)
      .then(() => api.fetchArchiveTimesBatch([url]))
      .then((fresh) => {
        if (fresh[url]) {
          setVertices((prev) =>
            prev.map((v) =>
              v.vertex.rendered === url ? { ...v, archiveTimes: fresh[url] } : v,
            ),
          );
        }
      })
      .catch((err) => {
        const code = (err as { body?: { errors?: { code?: string }[] } })?.body?.errors?.[0]?.code;
        if (code === 'CAPTURE_QUOTA_EXCEEDED') {
          alert('Daily server-capture quota reached. Try again tomorrow (resets at midnight UTC).');
        } else {
          alert(`Server capture failed: ${api.apiErrorMessage(err)}`);
        }
      })
      .finally(() => {
        setPendingBackendCapture((prev) => {
          const next = new Set(prev);
          next.delete(url);
          return next;
        });
        api
          .fetchArchiveSourcesToday()
          .then((r) => setCaptureQuota(r.captureQuota))
          .catch(() => {});
      });
  }

  async function handleToggleShareAll(url: string, currentMode: string, e: Event) {
    e.stopPropagation();
    const checkbox = e.target as HTMLInputElement;
    const newMode = checkbox.checked ? 'AllArchives' : 'OnlySharedArchives';

    try {
      await api.updateArchiveShareMode(url, newMode);

      // Update local state
      setVertices((prev) =>
        prev.map((v) =>
          v.vertex.rendered === url ? { ...v, archiveShareMode: newMode } : v
        )
      );

      // Refresh archive times to get updated share info
      const fresh = await api.fetchArchiveTimesBatch([url]);
      if (fresh[url]) {
        setVertices((prev) =>
          prev.map((v) =>
            v.vertex.rendered === url ? { ...v, archiveTimes: fresh[url] } : v
          )
        );
      }
    } catch (err) {
      console.error('[VertexList] Failed to update share mode:', err);
      // Revert checkbox on error
      checkbox.checked = !checkbox.checked;
    }
  }

  async function handleCopyClick(url: string, e: Event) {
    e.stopPropagation();
    const btn = e.currentTarget as HTMLButtonElement;
    await copyWithFeedback(btn, url);
  }

  // Vertex sets functions
  async function toggleSetsDropdown(vertexUrl: string, e: Event) {
    e.stopPropagation();
    e.preventDefault();

    if (setsDropdownOpen === vertexUrl) {
      setSetsDropdownOpen(null);
      setSetsSearchQuery('');
      setCreatingNewSet(false);
      setNewSetName('');
    } else {
      // Open the dropdown immediately so the user sees a response; fetch the full list in the
      // background and render a loading row until it arrives.
      setSetsDropdownOpen(vertexUrl);
      setOpenDropdown(null);
      if (!allSetsLoaded) {
        setSetsLoading(true);
        try {
          const all = await api.fetchAllVertexSets();
          setVertexSets((prev) => mergeSetsById(prev, all));
          setAllSetsLoaded(true);
        } catch (err) {
          console.error('Failed to load vertex sets:', err);
        } finally {
          setSetsLoading(false);
        }
      }
    }
  }

  function getFilteredSets() {
    if (!setsSearchQuery.trim()) return vertexSets;
    const query = setsSearchQuery.toLowerCase();
    return vertexSets.filter((s) => s.vertexSet.name.toLowerCase().includes(query));
  }

  async function handleSetsSearchKeyDown(vertexUrl: string, e: KeyboardEvent) {
    const filtered = getFilteredSets();
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (filtered.length === 0) return;
      setHighlightedSetIndex((prev) => Math.min(prev + 1, filtered.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (filtered.length === 0) return;
      setHighlightedSetIndex((prev) => Math.max(prev - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      const effectiveIdx =
        highlightedSetIndex >= 0
          ? highlightedSetIndex
          : filtered.length === 1
            ? 0
            : -1;
      if (effectiveIdx >= 0 && effectiveIdx < filtered.length) {
        e.preventDefault();
        const setData = filtered[effectiveIdx];
        const isInSet = setData.vertices.some((v) => v.vertex.rendered === vertexUrl);
        if (isInSet) {
          await handleRemoveFromSet(setData.vertexSet.id, vertexUrl, e);
        } else {
          await handleAddToSet(setData.vertexSet.id, vertexUrl, e);
        }
      } else if (filtered.length === 0 && setsSearchQuery.trim()) {
        e.preventDefault();
        setNewSetName(setsSearchQuery.trim());
        setCreatingNewSet(true);
      }
    } else if (e.key === 'Escape') {
      setSetsDropdownOpen(null);
      setSetsSearchQuery('');
    }
  }

  async function handleAddToSet(setId: string, vertexUrl: string, e: Event) {
    e.stopPropagation();
    try {
      await api.addVertexToSet(setId, vertexUrl);
      // Find the vertex object from our loaded vertices
      const vertexData = vertices.find((v) => v.vertex.rendered === vertexUrl);
      if (vertexData) {
        const newElem = {
          vertex: vertexData.vertex,
          createdAt: new Date().toISOString(),
          position: '',
        };
        setVertexSets((prev) =>
          prev.map((s) =>
            s.vertexSet.id === setId
              ? { ...s, vertices: [...s.vertices, newElem] }
              : s
          )
        );
      }
    } catch (err) {
      console.error('Failed to add vertex to set:', err);
    }
  }

  async function handleRemoveFromSet(setId: string, vertexUrl: string, e: Event) {
    e.stopPropagation();
    try {
      await api.removeVertexFromSet(setId, vertexUrl);
      // Update local state
      setVertexSets((prev) =>
        prev.map((s) =>
          s.vertexSet.id === setId
            ? { ...s, vertices: s.vertices.filter((v) => v.vertex.rendered !== vertexUrl) }
            : s
        )
      );
    } catch (err) {
      console.error('Failed to remove vertex from set:', err);
    }
  }

  async function handleCreateNewSetWithVertex(vertexUrl: string, e: Event) {
    e.stopPropagation();
    if (!newSetName.trim()) return;

    try {
      const newSet = await api.createVertexSet({
        name: newSetName.trim(),
        description: null,
      });
      await api.addVertexToSet(newSet.vertexSet.id, vertexUrl);
      // Find the vertex object from our loaded vertices
      const vertexData = vertices.find((v) => v.vertex.rendered === vertexUrl);
      if (vertexData) {
        newSet.vertices = [{
          vertex: vertexData.vertex,
          createdAt: new Date().toISOString(),
          position: '',
        }];
      }
      setVertexSets((prev) => [newSet, ...prev]);
      setCreatingNewSet(false);
      setNewSetName('');
      setSetsSearchQuery('');
    } catch (err) {
      console.error('Failed to create set:', err);
    }
  }

  function formatDate(isoDate: string): string {
    const date = new Date(isoDate);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  function formatArchiveDate(isoDate: string): string {
    const date = new Date(isoDate);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  if (loading) {
    return (
      <div class="loading" style="padding: 60px 20px; color: #6c757d; font-size: 18px;">
        Loading your vertices...
      </div>
    );
  }

  if (error) {
    return (
      <div class="error-message" style="padding: 20px; color: #dc3545;">
        {error}
      </div>
    );
  }

  if (vertices.length === 0) {
    return (
      <div class="empty-state" style="padding: 60px 20px; text-align: center; color: #777;">
        <p>No vertices found.</p>
        <p class="subtitle">Install the Chrome extension to start annotating links, images, and pages.</p>
        <OutlinedButton onClick={() => window.open(CHROME_EXTENSION_URL, '_blank')}>
          Add to Chrome
        </OutlinedButton>
      </div>
    );
  }

  function handleAuthorityClick(authority: string) {
    onViewModeChange('authority', authority);
  }

  function handleBackToAuthorities() {
    onViewModeChange('authority', null);
  }

  function toggleViewMode() {
    const newMode = effectiveViewMode === 'flat' ? 'authority' : 'flat';
    onViewModeChange(newMode, null);
  }

  const quotaReached = captureQuota?.status === 'reached';
  const quotaHintText =
    captureQuota?.status === 'remaining' || captureQuota?.status === 'reached'
      ? `${captureQuota.limit - captureQuota.used} remaining`
      : null;
  const captureTitle = (() => {
    if (captureQuota?.status === 'reached') {
      return `Daily server-capture quota reached (${captureQuota.limit} per day). Try again tomorrow.`;
    }
    if (captureQuota?.status === 'remaining') {
      const remaining = captureQuota.limit - captureQuota.used;
      return `${remaining} of ${captureQuota.limit} server captures remaining today. Beta (β): cookie, region and language banners may still get in the way.`;
    }
    return 'Ask the server to capture this URL. Beta (β): cookie, region and language banners may still get in the way.';
  })();

  return (
    <div>
      {/* Stats and View Toggle */}
      <StatsBar
        actions={
          <button
            class={`view-mode-toggle ${effectiveViewMode === 'authority' ? 'active' : ''}`}
            onClick={toggleViewMode}
            title={effectiveViewMode === 'flat' ? 'Group by authority' : 'Show flat list'}
          >
            <GridIcon />
          </button>
        }
      >
        {effectiveViewMode === 'authority' && selectedAuthority ? (
          <>
            <button class="back-to-authorities" onClick={handleBackToAuthorities}>
              ← Authorities
            </button>
            <span class="selected-authority">{selectedAuthority}</span>
            <span class="authority-vertex-count">
              ({displayVertices.length} {displayVertices.length === 1 ? 'vertex' : 'vertices'})
            </span>
          </>
        ) : effectiveViewMode === 'authority' ? (
          <>
            {authorityGroups.length} {authorityGroups.length === 1 ? 'authority' : 'authorities'}
            <span class="total-vertex-count">
              ({filteredVertices.length} {filteredVertices.length === 1 ? 'vertex' : 'vertices'})
            </span>
          </>
        ) : (
          <>
            {filteredVertices.length} {filteredVertices.length === 1 ? 'vertex' : 'vertices'}
          </>
        )}
      </StatsBar>

      {/* Authority Cards (when in authority mode and no authority selected) */}
      {effectiveViewMode === 'authority' && !selectedAuthority && (
        <div class="authority-grid">
          {authorityGroups.map((group) => (
            <button
              key={group.authority}
              class="authority-card"
              onClick={() => handleAuthorityClick(group.authority)}
            >
              {group.previewImage ? (
                <img
                  src={group.previewImage}
                  alt=""
                  class="authority-card-image"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : (
                <div class="authority-card-placeholder">
                  <GlobeOutlineIcon size={32} />
                </div>
              )}
              <div class="authority-card-info">
                <span class="authority-card-domain">{group.authority}</span>
                <span class="authority-card-count">
                  {group.vertices.length} {group.vertices.length === 1 ? 'vertex' : 'vertices'}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Vertex Cards (flat mode or when authority is selected) */}
      {(effectiveViewMode === 'flat' || selectedAuthority) && (
        <div class="vertices-list">
          {displayVertices.map((vertex) => {
          const archiveCount = vertex.archiveTimes?.length || 0;
          const timestamps = vertex.archiveTimes || [];
          const sortedTimestamps = [...timestamps].sort(
            (a, b) => new Date(b).getTime() - new Date(a).getTime()
          );
          const vertexUrl = vertex.vertex.rendered;

          return (
            <article key={vertexUrl} ref={getCardRef(vertexUrl)} class="vertex-card">
              {/* Main content */}
              <div class="vertex-main">
                {/* Preview or URL */}
                <VertexPreviewCard url={vertexUrl} preview={vertex.preview} />

                {/* Note */}
                <EditableNote
                  value={vertex.note}
                  onSave={(newNote) => handleSaveNote(vertexUrl, newNote)}
                />

                {/* Set membership: chips for each set on the left; "+" opens the add dropdown at the right. */}
                <div class="vertex-sets-chips">
                  {vertexSets
                    .filter((s) => s.vertices.some((elem) => elem.vertex.rendered === vertexUrl))
                    .map((s) => (
                      <span key={s.vertexSet.id} class="vertex-sets-chip">
                        <button
                          class="vertex-sets-chip-open"
                          title={`Open set: ${s.vertexSet.name}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenSet(s.vertexSet.id);
                          }}
                        >
                          <SetIcon size={12} strokeWidth={1.5} />
                          <span class="vertex-sets-chip-name">{s.vertexSet.name}</span>
                        </button>
                        <button
                          class="vertex-sets-chip-remove"
                          title={`Remove from set: ${s.vertexSet.name}`}
                          onClick={(e) => handleRemoveFromSet(s.vertexSet.id, vertexUrl, e)}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  <div class="vertex-sets-container">
                    <button
                      class="vertex-add-sets-btn"
                      title="add to set"
                      onClick={(e) => toggleSetsDropdown(vertexUrl, e)}
                    >
                      <SetIcon size={12} strokeWidth={1.5} />
                      <span class="vertex-add-sets-label">+ add to set</span>
                    </button>
                    {setsDropdownOpen === vertexUrl && (
                      <div class="vertex-sets-dropdown" onClick={(e) => e.stopPropagation()}>
                        <input
                          ref={setsSearchRef}
                          type="text"
                          placeholder="Search sets..."
                          value={setsSearchQuery}
                          onInput={(e) => setSetsSearchQuery((e.target as HTMLInputElement).value)}
                          onKeyDown={(e) => handleSetsSearchKeyDown(vertexUrl, e)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div class="vertex-sets-dropdown-list">
                          {creatingNewSet ? (
                            <div class="vertex-sets-new-input-container">
                              <input
                                type="text"
                                class="vertex-sets-new-input"
                                placeholder="New set name..."
                                value={newSetName}
                                onInput={(e) => setNewSetName((e.target as HTMLInputElement).value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleCreateNewSetWithVertex(vertexUrl, e);
                                  } else if (e.key === 'Escape') {
                                    setCreatingNewSet(false);
                                    setNewSetName('');
                                  }
                                }}
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                          ) : (
                            <button
                              class="vertex-sets-dropdown-item vertex-sets-new"
                              onClick={(e) => {
                                e.stopPropagation();
                                setNewSetName(setsSearchQuery.trim());
                                setCreatingNewSet(true);
                              }}
                            >
                              {setsSearchQuery.trim()
                                ? `+ Create "${setsSearchQuery.trim()}"`
                                : '+ New set...'}
                            </button>
                          )}
                          {setsLoading && !allSetsLoaded && (
                            <div class="vertex-sets-dropdown-loading">
                              Loading sets...
                            </div>
                          )}
                          {(() => {
                            const filtered = getFilteredSets();
                            const effectiveHighlight =
                              highlightedSetIndex >= 0
                                ? highlightedSetIndex
                                : filtered.length === 1
                                  ? 0
                                  : -1;
                            return filtered.map((setData, idx) => {
                              const isInSet = setData.vertices.some((v) => v.vertex.rendered === vertexUrl);
                              const isShared = setData.shareId !== null;
                              const isHighlighted = idx === effectiveHighlight;
                              return (
                                <button
                                  key={setData.vertexSet.id}
                                  data-set-idx={idx}
                                  class={`vertex-sets-dropdown-item ${isInSet ? 'in-set' : ''} ${isHighlighted ? 'highlighted' : ''}`}
                                  onClick={(e) =>
                                    isInSet
                                      ? handleRemoveFromSet(setData.vertexSet.id, vertexUrl, e)
                                      : handleAddToSet(setData.vertexSet.id, vertexUrl, e)
                                  }
                                >
                                  <span class="vertex-sets-indicator">
                                    {isInSet && <CheckIcon />}
                                  </span>
                                  <span class="vertex-sets-name">
                                    {setData.vertexSet.name}
                                    {isShared && (
                                      <GlobeIcon size={12} className="vertex-sets-shared" title="Shared publicly" />
                                    )}
                                  </span>
                                </button>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Timestamp */}
                {vertex.updatedAt && (
                  <p class="vertex-timestamp" title={new Date(vertex.updatedAt).toLocaleString()}>
                    Updated {formatDate(vertex.updatedAt)}
                  </p>
                )}
              </div>

              {/* Marginnote (sidenote) with archive pill, sets, copy, delete */}
              <div class="vertex-marginnote">
                {/* Archive pill */}
                <div class="archive-pill-container">
                  <button
                    class={
                      (archiveCount > 0 ? 'archive-count-pill active' : 'archive-count-pill')
                      + (pendingBackendCapture.has(vertexUrl) ? ' archive-count-pill-pending' : '')
                    }
                    title={
                      pendingBackendCapture.has(vertexUrl)
                        ? 'Server capture in progress...'
                        : archiveCount === 1 ? '1 archive' : `${archiveCount} archives`
                    }
                    onClick={(e) => toggleArchiveDropdown(vertexUrl, e)}
                  >
<ArchiveIcon size={14} strokeWidth={1.5} />
                    <span>{archiveCount > 0 ? archiveCount.toString() : '0'}</span>
                  </button>

                  {/* Dropdown */}
                  {openDropdown === vertexUrl && (
                    <div class="archive-dropdown">
                      {/* Share all toggle */}
                      <label
                        class="archive-dropdown-share-all"
                        title="Automatically share all new archives for this vertex"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={vertex.archiveShareMode === 'AllArchives'}
                          onChange={(e) => handleToggleShareAll(vertexUrl, vertex.archiveShareMode, e)}
                        />
                        <span>Share all archives</span>
                      </label>

                      <div class="archive-dropdown-separator" />

                      {/* Capture on server (headless Chromium) */}
                      <button
                        class="archive-dropdown-backend-capture"
                        onClick={(e) => handleBackendCapture(vertexUrl, e)}
                        disabled={pendingBackendCapture.has(vertexUrl) || quotaReached}
                        title={captureTitle}
                      >
                        <span>
                          {pendingBackendCapture.has(vertexUrl) ? 'Capturing…' : 'Server capture'}
                          {quotaHintText && <span class="quota-hint">{quotaHintText}</span>}
                        </span>
                        <span class="beta-badge">β</span>
                      </button>

                      {/* Separator if there are existing archives */}
                      {archiveCount > 0 && <div class="archive-dropdown-separator" />}

                      {/* List of existing archives */}
                      {sortedTimestamps.map((timestamp) => {
                        const shareInfo = window.archiveShareInfo?.[vertexUrl]?.[timestamp];
                        const isShared = shareInfo?.shareId !== null && shareInfo?.shareId !== undefined;
                        const note = shareInfo?.note ?? null;

                        return (
                          <button
                            key={timestamp}
                            class="archive-dropdown-item"
                            title={note ?? undefined}
                            onClick={(e) => {
                              e.stopPropagation();
                              onViewArchive(vertexUrl, timestamp, sortedTimestamps);
                            }}
                          >
                            {formatArchiveDate(timestamp)}
                            {isShared && (
                              <span class="archive-shared-indicator">
                                {' '}<GlobeIcon size={12} title="Shared publicly" />
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Copy button */}
                <button
                  class="vertex-copy-btn"
                  title="Copy URL"
                  onClick={(e) => handleCopyClick(vertexUrl, e)}
                >
                  <CopyIcon />
                </button>

                {/* Graph button - linear chain icon (source -> V -> target) */}
                <button
                  class="vertex-graph-btn"
                  title="View graph"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenGraph(vertexUrl);
                  }}
                >
                  <EdgeChainIcon />
                </button>

                {/* Delete button */}
                <button
                  class="vertex-delete-btn"
                  title="Delete vertex"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteVertex(vertexUrl);
                  }}
                >
                  <DeleteIcon />
                </button>
              </div>
            </article>
          );
        })}
        </div>
      )}
    </div>
  );
}
